import { Box, Card, styled, Avatar, useTheme } from "@mui/material";
import Shimmer from "components/Shimmer";
import useSession from "hooks/call-queue/useSession";

const VideoCard = styled(Card)(({ theme }) => ({
    borderRadius: '16px',
    boxShadow: `0 4px 20px rgba(0, 0, 0, 0.1)`,
    height: '100%',
    width: '100%',
    backgroundColor: '#1a1a1a',
    position: 'relative',
    overflow: 'hidden'
}));

const ChatCard = styled(Card)(({ theme }) => ({
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    borderRadius: 0,
    boxShadow: 'none',
    backgroundColor: theme.palette.background.paper,
    border: 'none'
}));

const ActionButton = styled(Box)(({ theme }) => ({
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    backdropFilter: 'blur(10px)',
    border: '1px solid rgba(255, 255, 255, 0.2)'
}));

const FloatingControls = styled(Box)(({ theme }) => ({
    position: 'absolute',
    bottom: '24px',
    left: '50%',
    transform: 'translateX(-50%)',
    display: 'flex',
    gap: '8px',
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    backdropFilter: 'blur(10px)',
    borderRadius: '24px',
    padding: '8px 16px',
    border: '1px solid rgba(255, 255, 255, 0.1)'
}));

const ConnectionIndicator = styled(Box)(({ theme }) => ({
    position: 'absolute',
    top: '16px',
    left: '16px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    backgroundColor: 'rgba(76, 175, 80, 0.9)',
    color: 'white',
    padding: '4px 12px',
    borderRadius: '16px',
    fontSize: '12px'
}));

const UserAvatar = styled(Avatar)(({ theme }) => ({
    width: '120px',
    height: '120px',
    backgroundColor: theme.palette.primary.main,
    fontSize: '32px',
    fontWeight: 'bold'
}));

const ChatHeader = styled(Box)(({ theme }) => ({
    padding: '16px',
    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
    borderBottom: `1px solid ${theme.palette.divider}`,
    display: 'flex',
    alignItems: 'center',
    gap: '12px'
}));

const MessageBubble = styled(Box)(({ theme }) => ({
    height: '40px',
    borderRadius: '20px',
    marginBottom: '8px',
    maxWidth: '70%'
}));

const VideoCallPlaceholder = () => {
    const { session } = useSession();
    const theme = useTheme();

    // If session type is CHAT, show only the chat skeleton (like video call skeleton structure)
    if (session?.sessionType === "CHAT") {
        return (
            <Box sx={{
                height: '94vh',
                backgroundColor: '#f5f5f5', // Match skeleton background
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                padding: '16px', // Add padding like skeleton
                gap: '16px', // Add gap like skeleton
            }}>
                {/* Chat Panel Only - Skeleton structure like video call */}
                <Box sx={{
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    backgroundColor: theme.palette.background.paper,
                    borderRadius: '0',
                    boxShadow: 'none',
                    border: 'none',
                    position: 'relative',
                    height: '100%'
                }}>
                    <ChatCard>
                        {/* Chat Header */}
                        <ChatHeader>
                            <Shimmer sx={{ width: '40px', height: '40px', borderRadius: '50%' }} />
                            <Box sx={{ flex: 1 }}>
                                <Shimmer sx={{ width: '120px', height: '16px', mb: 0.5 }} />
                                <Shimmer sx={{ width: '80px', height: '12px' }} />
                            </Box>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                                <Shimmer sx={{ width: '32px', height: '32px', borderRadius: '50%' }} />
                                <Shimmer sx={{ width: '32px', height: '32px', borderRadius: '50%' }} />
                                <Shimmer sx={{ width: '32px', height: '32px', borderRadius: '50%' }} />
                            </Box>
                        </ChatHeader>

                        {/* Chat Messages Area */}
                        <Box sx={{ 
                            flex: 1, 
                            padding: '16px',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '12px',
                            overflowY: 'auto'
                        }}>
                            {/* Welcome Message */}
                            <Box sx={{ 
                                display: 'flex', 
                                justifyContent: 'center',
                                mb: 2
                            }}>
                                <Box sx={{
                                    backgroundColor: 'rgba(0, 0, 0, 0.05)',
                                    borderRadius: '12px',
                                    padding: '8px 16px'
                                }}>
                                    <Shimmer sx={{ width: '160px', height: '14px' }} />
                                </Box>
                            </Box>

                            {/* Message Bubbles */}
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                                <MessageBubble sx={{ backgroundColor: '#e3f2fd', alignSelf: 'flex-end' }}>
                                    <Shimmer sx={{ width: '100%', height: '100%', borderRadius: '20px' }} />
                                </MessageBubble>
                            </Box>

                            <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                                <MessageBubble sx={{ backgroundColor: '#f5f5f5', alignSelf: 'flex-start' }}>
                                    <Shimmer sx={{ width: '100%', height: '100%', borderRadius: '20px' }} />
                                </MessageBubble>
                            </Box>

                            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                                <MessageBubble sx={{ backgroundColor: '#e3f2fd', alignSelf: 'flex-end', width: '60%' }}>
                                    <Shimmer sx={{ width: '100%', height: '100%', borderRadius: '20px' }} />
                                </MessageBubble>
                            </Box>

                            <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                                <MessageBubble sx={{ backgroundColor: '#f5f5f5', alignSelf: 'flex-start', width: '80%' }}>
                                    <Shimmer sx={{ width: '100%', height: '100%', borderRadius: '20px' }} />
                                </MessageBubble>
                            </Box>
                        </Box>

                        {/* Chat Input Area */}
                        <Box sx={{ 
                            padding: '16px',
                            backgroundColor: '#f9f9f9',
                            borderTop: '1px solid #e0e0e0'
                        }}>
                            <Box sx={{ 
                                display: 'flex',
                                gap: '12px',
                                alignItems: 'center'
                            }}>
                                <Box sx={{ flex: 1 }}>
                                    <Shimmer sx={{ 
                                        width: '100%', 
                                        height: '40px', 
                                        borderRadius: '20px' 
                                    }} />
                                </Box>
                                <Shimmer sx={{ 
                                    width: '40px', 
                                    height: '40px', 
                                    borderRadius: '50%' 
                                }} />
                                <Shimmer sx={{ 
                                    width: '40px', 
                                    height: '40px', 
                                    borderRadius: '50%' 
                                }} />
                            </Box>
                        </Box>
                    </ChatCard>
                </Box>
            </Box>
        );
    }

    // Default video call placeholder with both video and chat areas
    return (
        <Box sx={{ 
            height: '94vh', 
            backgroundColor: '#f5f5f5', 
            display: 'flex', 
            flexDirection: 'column', 
            overflow: 'hidden',
            padding: '16px',
            gap: '16px'
        }}>
            {/* Main Content Area */}
            <Box sx={{ 
                flex: 1, 
                display: 'flex',
                gap: '16px',
                minHeight: 0
            }}>
                {/* Video Area */}
                <Box sx={{ flex: 1, position: 'relative' }}>
                    <VideoCard>
                        {/* Connection Status Indicator */}
                        <ConnectionIndicator>
                            <Shimmer sx={{ width: '8px', height: '8px', borderRadius: '50%' }} />
                            <Shimmer sx={{ width: '60px', height: '12px' }} />
                        </ConnectionIndicator>

                        {/* Main Video Content */}
                        <Box sx={{ 
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexDirection: 'column',
                            background: 'linear-gradient(180deg, rgba(0,0,0,0.3) 0%, rgba(0,0,0,0) 20%, rgba(0,0,0,0) 80%, rgba(0,0,0,0.3) 100%)'
                        }}>
                            {/* User Avatar Placeholder */}
                            <UserAvatar>
                                <Shimmer sx={{ width: '100%', height: '100%', borderRadius: '50%' }} />
                            </UserAvatar>
                            
                            {/* User Name */}
                            <Box sx={{ mt: 2, mb: 1 }}>
                                <Shimmer sx={{ width: '140px', height: '24px', borderRadius: '4px' }} />
                            </Box>
                            
                            {/* Connection Status */}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Shimmer sx={{ width: '16px', height: '16px' }} />
                                <Shimmer sx={{ width: '100px', height: '14px' }} />
                            </Box>
                        </Box>

                        {/* Participant Info (Bottom Left) */}
                        <Box sx={{
                            position: 'absolute',
                            bottom: '16px',
                            left: '16px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            backgroundColor: 'rgba(0, 0, 0, 0.6)',
                            padding: '8px 12px',
                            borderRadius: '20px',
                            backdropFilter: 'blur(8px)'
                        }}>
                            <Shimmer sx={{ width: '32px', height: '32px', borderRadius: '50%' }} />
                            <Shimmer sx={{ width: '80px', height: '14px' }} />
                            <Shimmer sx={{ width: '16px', height: '16px' }} />
                        </Box>

                        {/* Floating Controls */}
                        <FloatingControls>
                            <ActionButton>
                                <Shimmer sx={{ width: '100%', height: '100%', borderRadius: '50%' }} />
                            </ActionButton>
                            <ActionButton>
                                <Shimmer sx={{ width: '100%', height: '100%', borderRadius: '50%' }} />
                            </ActionButton>
                            <ActionButton>
                                <Shimmer sx={{ width: '100%', height: '100%', borderRadius: '50%' }} />
                            </ActionButton>
                            <ActionButton>
                                <Shimmer sx={{ width: '100%', height: '100%', borderRadius: '50%' }} />
                            </ActionButton>
                            <ActionButton>
                                <Shimmer sx={{ width: '100%', height: '100%', borderRadius: '50%' }} />
                            </ActionButton>
                        </FloatingControls>
                    </VideoCard>
                </Box>

                {/* Chat Panel */}
                <Box sx={{ width: '400px', maxWidth: '400px' }}>
                    <ChatCard>
                        {/* Chat Header */}
                        <ChatHeader>
                            <Shimmer sx={{ width: '40px', height: '40px', borderRadius: '50%' }} />
                            <Box sx={{ flex: 1 }}>
                                <Shimmer sx={{ width: '120px', height: '16px', mb: 0.5 }} />
                                <Shimmer sx={{ width: '80px', height: '12px' }} />
                            </Box>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                                <Shimmer sx={{ width: '32px', height: '32px', borderRadius: '50%' }} />
                                <Shimmer sx={{ width: '32px', height: '32px', borderRadius: '50%' }} />
                                <Shimmer sx={{ width: '32px', height: '32px', borderRadius: '50%' }} />
                            </Box>
                        </ChatHeader>

                        {/* Chat Messages Area */}
                        <Box sx={{ 
                            flex: 1, 
                            padding: '16px',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '12px',
                            overflowY: 'auto'
                        }}>
                            {/* Welcome Message */}
                            <Box sx={{ 
                                display: 'flex', 
                                justifyContent: 'center',
                                mb: 2
                            }}>
                                <Box sx={{
                                    backgroundColor: 'rgba(0, 0, 0, 0.05)',
                                    borderRadius: '12px',
                                    padding: '8px 16px'
                                }}>
                                    <Shimmer sx={{ width: '160px', height: '14px' }} />
                                </Box>
                            </Box>

                            {/* Message Bubbles */}
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                                <MessageBubble sx={{ backgroundColor: '#e3f2fd', alignSelf: 'flex-end' }}>
                                    <Shimmer sx={{ width: '100%', height: '100%', borderRadius: '20px' }} />
                                </MessageBubble>
                            </Box>

                            <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                                <MessageBubble sx={{ backgroundColor: '#f5f5f5', alignSelf: 'flex-start' }}>
                                    <Shimmer sx={{ width: '100%', height: '100%', borderRadius: '20px' }} />
                                </MessageBubble>
                            </Box>

                            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                                <MessageBubble sx={{ backgroundColor: '#e3f2fd', alignSelf: 'flex-end', width: '60%' }}>
                                    <Shimmer sx={{ width: '100%', height: '100%', borderRadius: '20px' }} />
                                </MessageBubble>
                            </Box>

                            <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                                <MessageBubble sx={{ backgroundColor: '#f5f5f5', alignSelf: 'flex-start', width: '80%' }}>
                                    <Shimmer sx={{ width: '100%', height: '100%', borderRadius: '20px' }} />
                                </MessageBubble>
                            </Box>
                        </Box>

                        {/* Chat Input Area */}
                        <Box sx={{ 
                            padding: '16px',
                            backgroundColor: '#f9f9f9',
                            borderTop: '1px solid #e0e0e0'
                        }}>
                            <Box sx={{ 
                                display: 'flex',
                                gap: '12px',
                                alignItems: 'center'
                            }}>
                                <Box sx={{ flex: 1 }}>
                                    <Shimmer sx={{ 
                                        width: '100%', 
                                        height: '30px', 
                                        borderRadius: '20px' 
                                    }} />
                                </Box>
                                <Shimmer sx={{ 
                                    width: '40px', 
                                    height: '30px', 
                                    borderRadius: '50%' 
                                }} />
                                <Shimmer sx={{ 
                                    width: '40px', 
                                    height: '30px', 
                                    borderRadius: '50%' 
                                }} />
                            </Box>
                        </Box>
                    </ChatCard>
                </Box>
            </Box>
        </Box>
    )
}

export default VideoCallPlaceholder;